import { useRef } from "react";
import type { DatasetField, DatasetOption } from "@/api/datasets";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
} from "@/components/ui";
import { useT } from "@/i18n";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";

/** What a field's header menu asks of the table. */
export interface DatasetFieldActions {
  rename: (field: DatasetField) => void;
  remove: (field: DatasetField) => void;
  removeOption: (field: DatasetField, option: DatasetOption) => void;
}

/**
 * A field's header menu: Rename, Delete field and, for a select field,
 * Remove of each option. A table keeps at least one field and a select
 * field at least one option, so those last ones are not offered.
 */
export function DatasetFieldMenu({
  field,
  canDelete,
  actions,
}: {
  field: DatasetField;
  canDelete: boolean;
  actions: DatasetFieldActions;
}) {
  const t = useT(framesDatasetMessages);
  const options = field.type === "select" ? (field.options ?? []) : [];
  // A chosen item opens a dialog: focus goes there, not back to the trigger.
  const chose = useRef(false);
  const choose = (action: () => void) => () => {
    chose.current = true;
    action();
  };

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) chose.current = false;
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="iconSm"
          className="shrink-0 opacity-0 focus-visible:opacity-100 group-hover/header:opacity-100 data-[state=open]:opacity-100"
          aria-label={t("fieldMenu", { field: field.name })}
          data-testid={`dataset-field-menu-${field.id}`}
        >
          <Icons.ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-48"
        onCloseAutoFocus={(event) => chose.current && event.preventDefault()}
      >
        <DropdownMenuItem
          icon={<Icons.Edit2 />}
          onSelect={choose(() => actions.rename(field))}
          data-testid={`dataset-field-rename-${field.id}`}
        >
          {t("renameField")}
        </DropdownMenuItem>
        <DropdownMenuItem
          destructive
          disabled={!canDelete}
          icon={<Icons.Trash2 />}
          onSelect={choose(() => actions.remove(field))}
          data-testid={`dataset-field-delete-${field.id}`}
        >
          {t("deleteField")}
        </DropdownMenuItem>
        {options.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("options")}</DropdownMenuLabel>
            {options.map((option) => (
              <DropdownMenuItem
                key={option.id}
                destructive
                disabled={options.length <= 1}
                icon={<Icons.Minus />}
                onSelect={choose(() => actions.removeOption(field, option))}
                data-testid={`dataset-field-option-remove-${field.id}-${option.id}`}
              >
                {t("removeOption", { label: option.label })}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
